/**
 * UpSurv 2.0 — the sync engine.
 *
 * Queue-and-drain over the outbox in store.ts. The surveyor's device is the
 * system of record while the survey is being taken; this file is the part that
 * gets that record to Cloudflare afterwards, without ever being allowed to lose
 * it on the way.
 *
 * ══ THE CONFLICT RULE (brief §4) ═════════════════════════════════════════════
 *
 *   "device wins until first successful sync; server is append-only for
 *    submitted surveys."
 *
 * That sentence is implemented as an ORDERED ladder — `CONFLICT_PRECEDENCE`
 * below — rather than as a chain of ifs, because the order between its two
 * clauses is the whole decision and it deserves to be readable and testable.
 * `resolveConflict()` walks the ladder and returns the first rule that fires,
 * naming it. Nothing else in the codebase decides who wins.
 *
 * The order chosen, and why R2 sits above R3: a device that has never synced is
 * holding work the server has never seen. If the server's copy happens to be a
 * sealed submission, letting the server win would delete a day of basement work
 * to make room for a document the device could have read anyway. So the device
 * keeps its copy; the seal is still absolutely enforced, but by the SERVER
 * refusing the push (`outcome: 'sealed'`), at which point the device records the
 * seal, stops offering revisions — and still does not destroy anything local.
 * The invariant that matters ("a submitted survey is never overwritten") holds
 * either way; the ordering only decides which side loses data, and the answer to
 * that is never the surveyor.
 *
 * ══ MID-SYNC DISCONNECT (§6.3) ═══════════════════════════════════════════════
 *
 * Three properties, together:
 *
 *   1. An outbox row is deleted ONLY on an acknowledgement. Not on send, not on
 *      timeout, not on error. An interrupted drain therefore replays.
 *   2. Every row carries a stable client-generated id used as the idempotency
 *      key, so a replay after a LOST ACK — the server applied the write, the
 *      response never arrived — is deduped instead of double-writing.
 *   3. Rows left `in_flight` by a killed tab are returned to `queued` on
 *      `start()`, so "not lost" also means "actually retried".
 *
 * ══ THE CREATION NOTICE (spec §2.1) ══════════════════════════════════════════
 *
 * "Chip and Tim are notified immediately on creation — no job may exist they
 * don't know about." That notice is `POST /api/surveys`, and it is its own
 * outbox row (`kind: 'create'`), drained first and removed only by its own ack.
 *
 * It used to ride the document push as a side effect, which made it exactly as
 * durable as that row — and the document row is deleted by `ackSubmission`, by
 * `markSealed`, and by the guard that refuses to push a submitted document. A
 * survey created AND submitted with the radio off therefore drained its
 * submission having never announced itself, and the office had no way to know
 * there was anything to look for. Silence is the one failure mode this notice
 * cannot have, so it no longer depends on anything else surviving.
 *
 * ══ THE ASSIGNMENT INBOX (spec §2.1) ═════════════════════════════════════════
 *
 * "Assigned (usual)" is the PRIMARY way a survey comes into being; the field-
 * initiated path is the exception. `pullAssignments()` is that path on the
 * device, and src/main.tsx runs it on start, on reconnect, on `visibilitychange`
 * and on the periodic backstop — pulling BEFORE it pushes, so an incoming job is
 * not stuck behind a queue of photos.
 *
 * It shares the conflict ladder with `pull()` and adds nothing of its own: a
 * pulled document can only replace a local one when `resolveConflict()` says the
 * server won. A survey the device is still holding work for — dirty, or with any
 * outbox row at all — always wins.
 *
 * ══ PHOTOS ═══════════════════════════════════════════════════════════════════
 *
 * Photos upload one at a time, tagged survey / section / question (brief §3),
 * after the documents in the drain order, each inside its own try/catch. A
 * photo that will not upload cannot delay a document push and cannot stop the
 * next photo. It is also never dropped: it keeps retrying under exponential
 * backoff and, if it never lands, surfaces as a missing recommended shot (spec
 * §2.4 — warn, never block). The SUBMISSION travels behind the photos (D-050):
 * it defers while any photo of its survey is still queued, so the office —
 * which builds the PDF the moment a submission lands — finds every picture
 * already there. A photo that never lands therefore holds the seal, and the
 * surveyor's Needs-you screen is where that is resolved.
 *
 * ══ DETERMINISM ══════════════════════════════════════════════════════════════
 *
 * No `Date.now()`, no `Math.random()`, no `setTimeout` in the drain path. The
 * clock is injected and backoff is a pure function of the attempt count, so the
 * retry schedule in a test is the retry schedule in the field. Backoff carries
 * no jitter on purpose: a single device retrying its own outbox is not a
 * thundering herd, and reproducibility is worth more here than herd-avoidance.
 */

import {
  SurveyStore,
  isoAt,
  idempotencyKeyFor,
  neverReachedServer,
  outboxId,
  systemClock,
  type Clock,
  type OutboxItem,
  type OutboxKind,
  type StoredSurvey,
} from './store';
import { MAX_PHOTO_BYTES, isUndeclaredContentType, readPhotoBytes } from './photo-file';
import { emptySurveyDocument } from '../core/document';
import { isSubmittedStatus } from '../core/types';
import type { SurveyDocument, SurveyOrigin, SurveyStatus } from '../core/types';

/* ════════════════════════════ transport ════════════════════════════ */

/**
 * Errors thrown by a transport. `retryable` is advisory: the engine retries
 * EVERYTHING, because dropping a surveyor's work on a 400 is worse than
 * retrying one forever in full view of the status pill. The flag distinguishes
 * "the link is down" from "the server hated this payload" in the surfaced error.
 */
export class TransportError extends Error {
  readonly retryable: boolean;
  readonly status?: number;
  /**
   * The server's own `error` code, or a device-side code minted here.
   *
   * THE STATUS NUMBER IS NOT ENOUGH, and the 2026-08-10 incident is what proved
   * it. `400` covers both "this body is malformed" and "this body did not
   * finish arriving"; `409` covers both "this survey is sealed" and "its
   * document has not synced yet". One of each pair is permanent and the other
   * clears itself, and a numeric range cannot tell them apart — so the decision
   * is made from the CODE, which names the condition, and the range is only the
   * fallback for a server that did not send one.
   */
  readonly code?: string;

  constructor(
    message: string,
    options: { retryable?: boolean; status?: number; code?: string; cause?: unknown } = {},
  ) {
    super(message, options.cause !== undefined ? { cause: options.cause } : undefined);
    this.name = 'TransportError';
    this.retryable = options.retryable ?? true;
    if (options.status !== undefined) this.status = options.status;
    if (options.code !== undefined && options.code !== '') this.code = options.code;
  }
}

/**
 * Announce a field-initiated survey (spec §2.1). Carries the whole document
 * because the building name and the address ARE answers B1 / B2 — there is no
 * second place those facts live, and the transport reads them off the document
 * rather than being handed a copy that could disagree with it.
 */
export interface SurveyCreationRequest {
  /** The row id; stable per survey, so a replay after a lost ack is deduped. */
  idempotencyKey: string;
  surveyId: string;
  document: SurveyDocument;
  deviceId: string;
}

/**
 * `created` is the first call; `already_created` is the idempotent replay. Both
 * mean the same thing to the device — the office knows — which is why the ack
 * path does not branch on it.
 */
export type SurveyCreationResult = {
  outcome: 'created' | 'already_created';
};

export interface DocumentPushRequest {
  /** Stable per (survey, revision). Replay-safe. */
  idempotencyKey: string;
  surveyId: string;
  /** The device revision being pushed; travels back in the ack. */
  localRevision: number;
  document: SurveyDocument;
  deviceId: string;
}

/**
 * Mirrors `PutDocumentRevisionResult` in queries.ts:
 *   stored     — a new revision was appended.
 *   unchanged  — byte-identical to the head; the common lost-ack replay.
 *   sealed     — the survey is submitted; the server kept its revision.
 *   discarded  — the survey was withdrawn (D-012). The server will never accept
 *                another revision for it, so the push is undeliverable — but
 *                nothing local is destroyed, exactly as for `sealed`.
 */
export type DocumentPushResult =
  | { outcome: 'stored' | 'unchanged'; serverRevision: number }
  | { outcome: 'sealed'; serverRevision: number; conflict: boolean }
  | { outcome: 'discarded'; serverRevision: number };

/**
 * Withdraw a survey the server already knows about (D-012).
 *
 * Carries no document, and that is the point: by the time this is sent the
 * device has already forgotten the survey — `SurveyStore.discardSurvey` deletes
 * the document, the photo bytes and every other outbox row in one transaction —
 * so the row is a bare instruction with a stable id, and a replay after a lost
 * ack is idempotent server-side.
 */
export interface SurveyDiscardRequest {
  idempotencyKey: string;
  surveyId: string;
  deviceId: string;
}

/**
 * `discarded` is the first call, `already_discarded` the idempotent replay, and
 * `already_gone` covers a server that has no such survey at all. All three mean
 * the same thing to the device — the office is not holding this job — which is
 * why the ack path does not branch on them.
 */
export type SurveyDiscardResult = {
  outcome: 'discarded' | 'already_discarded' | 'already_gone';
};

/**
 * "Sealed on this phone; N photos still to come" (D-052 §3). Sent AHEAD of
 * the photos, so the office page can say so for the hours a queue can take
 * instead of reading *In progress* for a survey the surveyor has finished.
 * Nothing about the record travels here: the seal itself still goes last,
 * behind every picture (D-050), and the office acts on THAT.
 */
export interface SealNoticeRequest {
  idempotencyKey: string;
  surveyId: string;
  /** `document.submittedAt` — the instant the surveyor tapped Submit. */
  sealedAt: string;
  /** Photo rows of this survey still in the outbox at the moment of sending. */
  photosOwed: number;
  deviceId: string;
}

/**
 * One photo. Tagged survey / section / question per brief §3 — `r2Key` already
 * encodes the same tagging (photoR2Key in types.ts) and `sectionInstanceId`
 * carries the room / group / hoistway scope, so the object is addressable from
 * either side without a lookup.
 */
export interface PhotoUploadRequest {
  idempotencyKey: string;
  surveyId: string;
  photoId: string;
  sectionInstanceId: string;
  questionId?: string;
  shotListId?: string;
  /** 2.4.0, D-038 — carried into the meta JSON when the photo has one. */
  label?: string;
  r2Key: string;
  contentType: string;
  bytes: number;
  /**
   * When the surveyor took the picture — NOT when it uploaded. A photo captured
   * in a basement on Monday and drained on Wednesday must still say Monday; the
   * transport has no clock of its own precisely so it cannot substitute one.
   */
  capturedAt: string;
  /**
   * THE PICTURE, as bytes in memory — never a Blob handle (2026-08-19). The
   * engine reads the stored Blob and checks the length against `bytes` before
   * this request exists, so a transport is only ever handed a picture that is
   * actually there; and a `Uint8Array` body is serialised by the page itself,
   * which is the whole reason the iPhone's empty uploads cannot recur through
   * this interface. See src/db/photo-file.ts.
   */
  data: Uint8Array;
}

export type PhotoUploadResult = {
  /** `already_uploaded` is the lost-ack replay: the object is in R2 either way. */
  outcome: 'uploaded' | 'already_uploaded';
  r2Key: string;
};

/**
 * The thumbnail beside a picture (round 8.1, task 3): `PUT
 * /api/photos/:surveyId/:photoId/thumb`, raw JPEG bytes. No key travels —
 * the office derives it from the record's own `r2Key` — and no metadata
 * either, because the thumb IS metadata of a photo the office already holds.
 * A replay is idempotent by construction: the same bytes land on the same key.
 */
export interface PhotoThumbUploadRequest {
  surveyId: string;
  photoId: string;
  /** The thumb, as bytes in memory, for the same reason `PhotoUploadRequest.data` is. */
  data: Uint8Array;
}

export interface SubmissionRequest {
  idempotencyKey: string;
  surveyId: string;
  document: SurveyDocument;
  deviceId: string;
}

export type SubmissionResult = {
  outcome: 'submitted' | 'already_submitted';
  serverRevision: number;
};

export interface RemoteDocument {
  document: SurveyDocument;
  serverRevision: number;
  sealed: boolean;
}

/**
 * One row of the surveyor's assignment list — the LIFECYCLE record, not the
 * document. This is deliberately all the inbox needs to decide what to do:
 * `headRevision` says whether a document exists at all, `status` says whether it
 * is sealed, and `buildingName` / `address` are usually the entire content of a
 * fresh assignment (spec §2.1).
 */
export interface RemoteAssignment {
  surveyId: string;
  origin: SurveyOrigin;
  status: SurveyStatus;
  assignedTo?: string;
  createdBy?: string;
  buildingName?: string;
  address?: string;
  /** The office's city / state / ZIP (D-039), seeded as B2a / B2b / B2c. */
  city?: string;
  state?: string;
  zip?: string;
  /** 0 = the server has never received a document for this survey. */
  headRevision: number;
  createdAt: string;
  updatedAt: string;
}

export interface SyncTransport {
  /**
   * Required, not optional. A transport that cannot announce a survey would
   * lose spec §2.1's notice silently, and "the office never heard about the
   * job" is precisely the failure that must not be possible to configure.
   */
  createSurvey(request: SurveyCreationRequest): Promise<SurveyCreationResult>;
  pushDocument(request: DocumentPushRequest): Promise<DocumentPushResult>;
  uploadPhoto(request: PhotoUploadRequest): Promise<PhotoUploadResult>;
  /**
   * Required, like `uploadPhoto`: a transport that could send a picture but
   * not its thumb would leave every `photo_thumb` row with no call to make.
   * Resolves on a 201; throws a `TransportError` for anything else.
   */
  uploadPhotoThumb(request: PhotoThumbUploadRequest): Promise<void>;
  submitSurvey(request: SubmissionRequest): Promise<SubmissionResult>;
  /**
   * Required, not optional, for the same reason `createSurvey` is: a discard is
   * a promise the device has ALREADY kept locally — the survey is gone from
   * every list on the phone — and a transport that could not deliver it would
   * leave the office holding a job nobody is coming to do, silently and
   * forever.
   */
  discardSurvey(request: SurveyDiscardRequest): Promise<SurveyDiscardResult>;
  /**
   * Required, because the row is queued beside every submission and a
   * transport with no call to make for it would park every seal notice on the
   * Needs-you screen. Resolves once the office has recorded it — a survey the
   * office no longer holds, or already holds sealed, counts as recorded.
   */
  notifySealed(request: SealNoticeRequest): Promise<void>;
  /** Optional: pulling assignments. Absent transports simply cannot `pull()`. */
  fetchDocument?(surveyId: string): Promise<RemoteDocument | null>;
  /**
   * Optional: the surveyor's assignment list, scoped server-side to the token
   * this transport carries. Absent transports have no inbox and
   * `pullAssignments()` reports `skipped: 'unsupported'` rather than throwing —
   * an in-memory test transport is not a broken app.
   */
  listAssignments?(): Promise<RemoteAssignment[]>;
}

/* ════════════════════════════ backoff ════════════════════════════ */

export interface BackoffPolicy {
  baseMs: number;
  factor: number;
  maxMs: number;
}

/** 1s, 2s, 4s, 8s … capped at 5 minutes. */
export const DEFAULT_BACKOFF: BackoffPolicy = {
  baseMs: 1_000,
  factor: 2,
  maxMs: 5 * 60_000,
};

/**
 * Delay before retry number `attempts` (1 = the first retry, after one failure).
 * Pure: same input, same output, no clock and no jitter.
 */
export function backoffDelayMs(attempts: number, policy: BackoffPolicy = DEFAULT_BACKOFF): number {
  const n = Math.max(1, Math.trunc(attempts));
  const raw = policy.baseMs * Math.pow(policy.factor, n - 1);
  return Math.min(policy.maxMs, Math.round(raw));
}

/* ════════════════════════ the conflict ladder ════════════════════════ */

export type ConflictWinner = 'device' | 'server';

export interface ConflictContext {
  /** The server has acknowledged at least one document push for this survey. */
  everSynced: boolean;
  /** The device holds content the server has not acknowledged. */
  localDirty: boolean;
  /** The server holds any document at all for this survey. */
  serverHasDocument: boolean;
  /** The server's copy is a submission: append-only, permanently. */
  serverSealed: boolean;
  /**
   * Whether the device holds a copy of this survey AT ALL.
   *
   * Optional, defaulting to "yes", because every caller before the assignment
   * inbox was asking about a record it already had. It has to be stated because
   * the rest of the ladder is written to PROTECT local work, and "there is no
   * local work" is a different question from "the local work is older" — R2
   * would otherwise refuse every incoming assignment on the grounds that the
   * device had never synced the survey it has never heard of.
   */
  localExists?: boolean;
  /**
   * Whether the outbox still owes the server anything for this survey.
   *
   * Wider than `localDirty` on purpose: a survey can be clean and still hold a
   * queued photo whose bytes have never left the phone. Adopting a server copy
   * over that drops the PhotoRef the queued upload refers to, and the row is
   * then discarded as pointing at nothing.
   */
  localPending?: boolean;
}

export interface ConflictRule {
  /** Stable id, so a test can assert the ORDER and not just the outcome. */
  id: string;
  winner: ConflictWinner;
  when: (context: ConflictContext) => boolean;
  because: string;
}

/**
 * Brief §4, in precedence order. First match wins. The order IS the rule.
 */
export const CONFLICT_PRECEDENCE: readonly ConflictRule[] = [
  {
    // FIRST, above everything, including R2. Every rule below this one exists to
    // stop the server overwriting the surveyor's work; when the device has never
    // heard of the survey there is no work to overwrite, and refusing the server
    // copy here is how an assigned survey fails to arrive at all.
    id: 'R0-nothing-local-to-protect',
    winner: 'server',
    when: (c) => c.localExists === false,
    because:
      'the device holds no copy of this survey, so taking the server’s cannot overwrite ' +
      'anything — this is how an assignment reaches the phone (spec §2.1)',
  },
  {
    id: 'R1-no-server-document',
    winner: 'device',
    when: (c) => !c.serverHasDocument,
    because: 'the server holds no document for this survey, so there is nothing to conflict with',
  },
  {
    id: 'R2-device-wins-before-first-sync',
    winner: 'device',
    when: (c) => !c.everSynced,
    because:
      'brief §4: the device wins until its first successful sync — the server has never ' +
      'acknowledged a revision from this device, so its copy cannot be newer than the ' +
      'work in hand',
  },
  {
    id: 'R3-server-sealed-is-final',
    winner: 'server',
    when: (c) => c.serverSealed,
    because:
      'brief §4: the server is append-only for submitted surveys — a sealed submission is ' +
      'the record the estimate was built from and is never superseded',
  },
  {
    id: 'R4-device-wins-while-dirty',
    winner: 'device',
    when: (c) => c.localDirty,
    because:
      'the device holds unsynced edits; the server only holds revisions this device ' +
      'already pushed, so the local copy is strictly ahead',
  },
  {
    // Sits with R4 rather than after R5 because it is the same claim: the device
    // is holding something the server has not got. `dirty` says that about the
    // document; the outbox says it about everything else, photos included.
    id: 'R4b-device-wins-with-pending-outbox',
    winner: 'device',
    when: (c) => c.localPending === true,
    because:
      'the outbox still owes the server work for this survey — adopting the server copy ' +
      'would strand what those rows point at, photos first',
  },
  {
    id: 'R5-server-authoritative-when-clean',
    winner: 'server',
    when: () => true,
    because: 'the device has nothing unsynced, so the server copy is taken as current',
  },
];

export interface ConflictDecision {
  winner: ConflictWinner;
  ruleId: string;
  because: string;
}

/**
 * Not a ladder rule, and deliberately not one: a queued discard is not a claim
 * about whose COPY is newer, it is a decision that this device wants no copy at
 * all. Folding it into `CONFLICT_PRECEDENCE` would make every caller of
 * `resolveConflict()` — a pure function over five booleans — need an async
 * outbox read to build its context. It is reported as a decision so a pull's
 * result still explains itself in the same shape.
 */
export const DISCARD_PENDING_DECISION: ConflictDecision = {
  winner: 'device',
  ruleId: 'D-discard-pending',
  because:
    'this survey has been discarded on the device and the server has not been told yet, ' +
    'so the copy it is still listing must not be taken back (D-012)',
};

/** Walk the ladder. Total: R5 matches everything, so this never returns null. */
export function resolveConflict(context: ConflictContext): ConflictDecision {
  for (const rule of CONFLICT_PRECEDENCE) {
    if (rule.when(context)) {
      return { winner: rule.winner, ruleId: rule.id, because: rule.because };
    }
  }
  // Unreachable: R5's predicate is constant true. Kept so a future edit that
  // removes the catch-all fails loudly instead of returning undefined.
  throw new Error('resolveConflict: no rule matched — CONFLICT_PRECEDENCE lost its catch-all');
}

/**
 * Conflict context for a local record against a remote copy.
 *
 * `pendingOutbox` is the number of rows the outbox still holds for this survey.
 * It is a parameter rather than something read in here because this function is
 * pure and every caller already has the count (or can take one indexed count).
 */
export function conflictContextFor(
  local: StoredSurvey | undefined,
  remote: RemoteDocument | null,
  pendingOutbox = 0,
): ConflictContext {
  return {
    localExists: local !== undefined,
    everSynced: (local?.serverRevision ?? 0) > 0,
    localDirty: (local?.dirty ?? 0) === 1,
    localPending: pendingOutbox > 0,
    serverHasDocument: remote !== null,
    serverSealed: remote?.sealed ?? false,
  };
}

/* ════════════════════ permanent refusals, and what to say ═══════════════════ */

/**
 * The statuses that mean "the server refused THESE BYTES", as distinct from
 * "the server refused RIGHT NOW".
 *
 * Retrying an identical payload against any of these can only produce the same
 * answer, so a row that gets one is parked (store.parkOutboxItem) instead of
 * being re-sent on every drain trigger until the end of time.
 *
 * WHAT IS DELIBERATELY ABSENT, and why each one:
 *   401 — the device is not linked yet. Nothing is wrong with the payload; the
 *         moment a setup code is entered every queued row becomes deliverable,
 *         and parking them all would demand a manual retry per row for a
 *         condition that fixed itself.
 *   403 — authorization, which can change server-side without the device
 *         touching the payload (a re-assignment, a reactivated surveyor).
 *   404 / 409 — already handled as OUTCOMES further up (410 discarded, 409
 *         sealed, 409 survey_not_synced), where they mean something specific.
 *         409 `survey_not_synced` in particular resolves itself the moment the
 *         document push lands, which is one drain away.
 *   408 / 429 / 5xx — explicitly temporary.
 */
const PERMANENT_PAYLOAD_STATUSES: ReadonlySet<number> = new Set([400, 413, 415, 422]);

/**
 * Codes that mean "NOT YET", whatever status they arrived under.
 *
 * These OUTRANK the status set above, and that ordering is the fix for the
 * 2026-08-10 incident. Both of these conditions are answered with a status the
 * range-based rule reads as permanent, and both of them clear themselves:
 *
 *   incomplete_upload   the multipart body did not all arrive. A 4 MB photo on a
 *                       machine-room link that dropped partway. The photo was
 *                       never the problem — the same bytes land the moment the
 *                       link holds. Answered 400.
 *   survey_not_synced   the office has no row for this survey yet, so a photo
 *                       cannot be filed against it. Resolved by the document
 *                       push, which is one drain away and ordered ahead of
 *                       photos anyway. Answered 409.
 *
 * "Not yet" is not "never", and parking a row for it costs a deliverable.
 */
export const TRANSIENT_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'incomplete_upload',
  'survey_not_synced',
]);

/**
 * Device-side codes that mean "no request will ever fix this".
 *
 * `photo_bytes_missing` is the only one, and it is real: iOS evicts IndexedDB
 * blobs under storage pressure, and five 4 MB captures is enough pressure. The
 * PhotoRef survives, the picture does not, and no amount of retrying conjures
 * bytes back. It parks — LOUDLY, with a sentence that says the picture is gone
 * and the survey is not — because the alternative, which is what this code used
 * to do, was to delete the outbox row silently and leave a tile reading "Will
 * retry" over a photo nothing was ever going to send again.
 */
export const PHOTO_BYTES_MISSING = 'photo_bytes_missing';

/**
 * The thumb's counterpart (round 8.1, task 3), and deliberately a DEVICE
 * code even when the office is the one answering. Three findings park a
 * `photo_thumb` row under it: the thumb row is gone from this phone; the
 * office holds no photo to file the thumb against (404); the office holds
 * the survey but will not take a thumb for it now (409). None of them is a
 * transient link problem and none of them is a reason to touch the photo,
 * which is at the office or on its own way there. The row parks quietly —
 * nothing about the picture is lost, and the card never reads as stuck.
 */
export const PHOTO_THUMB_MISSING = 'photo_thumb_missing';
const DEVICE_PERMANENT_CODES: ReadonlySet<string> = new Set([
  PHOTO_BYTES_MISSING,
  PHOTO_THUMB_MISSING,
]);

/** The size cap in the units the sentence says it in. One registry, one number. */
const MAX_PHOTO_MB = Math.round(MAX_PHOTO_BYTES / (1024 * 1024));

/** True when the server named a condition that resolves itself. */
export function isTransientRefusal(error: unknown): boolean {
  if (!(error instanceof TransportError)) return false;
  return error.code !== undefined && TRANSIENT_REFUSAL_CODES.has(error.code);
}

export function isPermanentPayloadRefusal(error: unknown): boolean {
  if (!(error instanceof TransportError)) return false;
  // The code, when there is one, is the answer. The number is the fallback.
  if (isTransientRefusal(error)) return false;
  if (error.status === undefined) return false;
  return PERMANENT_PAYLOAD_STATUSES.has(error.status);
}

/**
 * Should this row stop being handed to the transport?
 *
 * Two ways to be true and they are not the same thing, which is why they are
 * named separately and why `SyncAttention.side` reports which one fired:
 * the OFFICE refused these bytes on their merits, or THIS PHONE cannot produce
 * them at all. The first is a payload question, the second is a storage one.
 */
export function shouldParkRow(error: unknown): boolean {
  if (error instanceof TransportError && error.code !== undefined) {
    if (DEVICE_PERMANENT_CODES.has(error.code)) return true;
  }
  return isPermanentPayloadRefusal(error);
}

/**
 * Was this row parked by the rule the 2026-08-10 fix corrected?
 *
 * Rows already sitting `blocked` on a phone carry the OLD message and no code —
 * the classification that parked them is not reachable any more, but the rows
 * are, and only a person can normally un-park one. This is what lets `start()`
 * re-arm them without the surveyor having to find and tap five separate photos
 * to undo a decision the app got wrong on their behalf.
 *
 * Matched narrowly and literally against the messages the corrected branches
 * used to mint. Anything else stays parked: re-arming a row that really is
 * unsendable is how a retry loop comes back.
 *
 * Three arms, and each is a different phone:
 *
 *   (400) expected multipart/form-data   the OLD phone talking to the OLD
 *                                        office — the incident itself.
 *   (400) did not arrive in one piece    the OLD phone talking to the NEW
 *                                        office. Pages deploys the API and the
 *                                        app together, but an installed PWA
 *                                        keeps its old shell until the service
 *                                        worker updates, so for one drain the
 *                                        old status-only rule reads the new
 *                                        `incomplete_upload` sentence and parks
 *                                        it. Without this arm those rows would
 *                                        stay parked under the wrong sentence
 *                                        after the phone caught up.
 *   (409) before its photos              belt and braces. The old rule never
 *                                        parked a 409 (409 was not in the
 *                                        permanent set), so no real phone holds
 *                                        this row; it is here so a future rule
 *                                        that does cannot strand one.
 */
export function wasMisparked(lastError: string | undefined): boolean {
  if (!lastError) return false;
  return (
    /\(400\)[^]*expected multipart\/form-data/.test(lastError) ||
    /\(400\)[^]*did not arrive in one piece/.test(lastError) ||
    /\(409\)[^]*before its photos/.test(lastError)
  );
}

/**
 * One parked row, in words a surveyor in a machine room can act on.
 *
 * The engineering detail is kept — the office and the logs need it — but it is
 * in `detail`, underneath. `reason` is the sentence on the phone, and it says
 * what to DO, because "invalid_body" told forty-two consecutive retries' worth
 * of nobody anything at all.
 */
export interface SyncAttention {
  /** The outbox row. Pass back to `retryBlocked` to try it once more. */
  itemId: string;
  kind: OutboxKind;
  surveyId: string;
  /** Present on photo rows — the capture that needs a decision. */
  photoId?: string;
  status?: number;
  /**
   * WHERE it failed. `'office'` means a response came back refusing it;
   * `'device'` means no response ever existed because the phone could not make
   * the request. Derived from `status`, which is present only when the office
   * answered — so this cannot drift away from the truth it reports.
   */
  side?: 'device' | 'office';
  /**
   * Whether "Try sending it again" can possibly help. False only when the thing
   * to send is gone from this phone; a screen that offers an action which cannot
   * work is worse than one that offers none.
   */
  canRetry?: boolean;
  /** Plain language, no jargon: what happened and what to do about it. */
  reason: string;
  /** The server's own words. For the office, and for a log. */
  detail: string;
}

/**
 * The content type out of a 415's stored message.
 *
 * `POST /api/photos` answers `content type "image/tiff" not accepted`, and
 * `ApiClient.fail` keeps that verbatim, so this is a parse of our own format
 * rather than of the network's. Anything else yields '' and the sentence simply
 * does not name a kind.
 */
export function refusedContentType(message: string | undefined): string {
  const match = /content type "([^"]{0,100})"/.exec(message ?? '');
  return match?.[1]?.trim().toLowerCase() ?? '';
}

/**
 * The 415 cause, in words rather than in MIME.
 *
 * `application/octet-stream` is the one worth translating rather than printing:
 * it is not a kind of picture at all, it is what a multipart part is stamped
 * with when the FILE CARRIED NO TYPE — which is exactly the live-camera defect,
 * and "the phone did not say what kind of picture it was" is both the truth and
 * something a person can repeat down a telephone.
 */
function pictureKindClause(message: string | undefined): string {
  const type = refusedContentType(message);
  if (!type) return '';
  if (isUndeclaredContentType(type)) {
    return ' The phone did not say what kind of picture it was.';
  }
  return ` This one is a ${type.replace(/^image\//, '')} picture, and the office can open JPEG, PNG, HEIC and WebP.`;
}

/**
 * D-015: calm. This is "one photo needs attention", never an alarm — the
 * survey is safe on the phone and nothing is lost, which is the first thing
 * each sentence says.
 *
 * ── AND IT SAYS WHY (2026-08-07) ─────────────────────────────────────────────
 *
 * What the design authority was left looking at, for days, was "the office
 * cannot accept it" and nothing else. That is not enough to act on: it does not
 * separate "this file is the wrong kind" from "this file is too big" from "the
 * office could not read what came with it", and those are three different next
 * moves. The server always knew which; the sentence threw it away.
 *
 * So every sentence now carries a CAUSE — and carries it in plain words, not in
 * the server's. The engineering half stays where it always was, in `detail`,
 * for the office and for a log; splicing `questionId — expected a question id`
 * onto a surveyor's screen would be swapping one unusable sentence for another.
 *
 * The 415 wording changed for a second reason. It used to end "take it again
 * with the camera" — and the live-camera defect made the camera the ONE source
 * that could not produce an acceptable file, so the app was instructing a person
 * to repeat, over and over, the exact thing that had just failed. It no longer
 * tells anyone to do something it cannot vouch for.
 */
export function plainReason(
  kind: OutboxKind,
  status: number | undefined,
  detail?: string,
): string {
  const thing = kind === 'photo' ? 'photo' : 'item';

  // A thumb is the small copy the shelf draws (round 8.1, task 3), and the
  // one sentence about it has to say what it is NOT: the photo. Whatever
  // parked it — this phone lost the small copy, or the office would not file
  // it — the picture itself is unaffected, and nothing needs retaking.
  if (kind === 'photo_thumb') {
    return 'The small preview of this photo could not be sent to the office. The photo itself is not affected — it is on your phone and at the office as usual. Nothing needs retaking.';
  }

  /*
   * ── WHICH SIDE FAILED, SAID OUT LOUD (2026-08-10) ──────────────────────────
   *
   * Every sentence below this block begins "The office could not…" — and until
   * today they were printed for failures the office never saw. `status` is
   * `undefined` when the request never got an answer: the phone threw before a
   * response existed. Blaming the office for that is not a wording nitpick, it
   * is the reason nobody could diagnose the 2026-08-10 incident from the screen:
   * the one sentence available could not distinguish "they refused it" from "it
   * never left", and those have opposite next moves.
   *
   * So the no-status case is answered FIRST, in its own words, naming the phone.
   */
  if (status === undefined) {
    if (kind === 'photo' && detail?.includes(PHOTO_BYTES_MISSING)) {
      return 'The picture itself is no longer on this phone — only the note that you took it. The phone cleared it to make room. Nothing else in the survey is affected. Take this one again.';
    }
    if (kind === 'photo') {
      return 'This photo never left the phone, so the office has not seen it and there is nothing wrong with the picture. It is still here — nothing is lost. Try once more where there is a signal.';
    }
    return 'This never left the phone, so the office has not seen it. Nothing is lost — it is still saved here. Try once more where there is a signal.';
  }

  if (status === 413) {
    return `This ${thing} is too big for the office to accept — the limit is ${MAX_PHOTO_MB} MB. It is still on your phone — nothing is lost. Delete it here and take it again, or leave it and tell the office.`;
  }
  if (status === 415) {
    return `The office cannot open this kind of picture file.${pictureKindClause(detail)} It is still on your phone — nothing is lost. Delete it here and take a new picture.`;
  }
  if (kind === 'photo') {
    return 'The office could not accept this photo — it could not read the details saved with it. It is still on your phone — nothing is lost. Try once more, or delete it here and take it again.';
  }
  return 'The office could not accept this — it could not read what this device sent. Nothing is lost — it is still saved on this phone. Try once more, or call the office.';
}

/* ═══════════════════════════ observable status ═══════════════════════════ */

/** Everything the always-visible sync pill (brief §2 / mockup) needs. */
export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  /** Outbox depth — documents + submissions + photos, parked rows included. */
  queued: number;
  /** The photo share of `queued`, so the pill can say "3 photos waiting". */
  queuedPhotos: number;
  /**
   * The share of `queued` that is parked and will not be retried on its own.
   *
   * Separate from `queued` because the pill has to distinguish "still uploading"
   * from "this one needs you". They call for different words, a different
   * colour, and — the point — a different action.
   */
  blocked: number;
  /** One entry per parked row, so a screen can say WHICH photo and WHY. */
  attention: readonly SyncAttention[];
  /** ISO instant of the last acknowledged write, or null if never. */
  lastSyncedAt: string | null;
  /**
   * Human-readable last TRANSIENT failure. Cleared by the next successful drain.
   *
   * Parked rows deliberately do NOT set this: a permanent refusal would pin the
   * pill in its red alarm state forever, which is exactly the behaviour that
   * teaches a surveyor the pill is noise. They are reported through `attention`.
   */
  lastError: string | null;
}

export type SyncStatusListener = (status: SyncStatus) => void;

export interface DrainError {
  itemId: string;
  kind: OutboxKind;
  message: string;
  retryable: boolean;
  /** True when the row was parked rather than scheduled for another attempt. */
  parked?: boolean;
}

export interface DrainReport {
  ran: boolean;
  skipped?: 'offline' | 'already_draining';
  attempted: number;
  delivered: number;
  /**
   * Rows put back without being sent, because sending them now could only fail.
   *
   * Not a failure and not a delivery: a photo whose survey the office has no row
   * for yet is not broken, it is early. Counting it as either would make the
   * report lie about the state of the queue.
   */
  deferred: number;
  failed: number;
  /** Rows still in the outbox when the drain returned. */
  remaining: number;
  errors: DrainError[];
}

export interface PullResult {
  applied: boolean;
  decision: ConflictDecision;
  serverRevision: number;
}

/** What happened to one assignment row. */
export type AssignmentOutcome =
  /** The device did not have it and now does. */
  | 'added'
  /** The device had it and took a newer server copy over it. */
  | 'adopted'
  /** The device's copy stood — it won the ladder, or it was already current. */
  | 'kept';

export interface AssignmentPullReport {
  ran: boolean;
  skipped?: 'offline' | 'already_pulling' | 'unsupported';
  /** Assignments the server listed for this surveyor. */
  seen: number;
  added: number;
  adopted: number;
  kept: number;
  errors: string[];
}

/* ═════════════════════════════ the engine ═════════════════════════════ */

export interface SyncEngineOptions {
  transport: SyncTransport;
  /** Injected so a test's retry schedule is the field's retry schedule. */
  clock?: Clock;
  /** Stamped on pushes for sync forensics; never used for conflict resolution. */
  deviceId?: string;
  backoff?: BackoffPolicy;
  /** Initial connectivity. Field default is optimistic: try, then learn. */
  online?: boolean;
}

export class SyncEngine {
  private readonly store: SurveyStore;
  private readonly transport: SyncTransport;
  private readonly clock: Clock;
  private readonly deviceId: string;
  private readonly backoff: BackoffPolicy;

  private readonly listeners = new Set<SyncStatusListener>();
  private status: SyncStatus;
  /** The drain currently in progress, if any. Also the concurrency guard. */
  private active: Promise<DrainReport> | null = null;
  /** The assignment pull in progress, if any. Separate from `active` on
   *  purpose: a pull must not be blocked behind a long push queue, which is the
   *  whole reason the app pulls BEFORE it drains on start. */
  private pulling: Promise<AssignmentPullReport> | null = null;
  private unsubscribeStore: (() => void) | null = null;
  /** See `stableAttention` — keeps a rebuilt-but-identical list from notifying. */
  private attentionSignature = '';

  constructor(store: SurveyStore, options: SyncEngineOptions) {
    this.store = store;
    this.transport = options.transport;
    this.clock = options.clock ?? systemClock;
    this.deviceId = options.deviceId ?? 'device';
    this.backoff = options.backoff ?? DEFAULT_BACKOFF;
    this.status = {
      online: options.online ?? true,
      syncing: false,
      queued: 0,
      queuedPhotos: 0,
      blocked: 0,
      attention: EMPTY_ATTENTION,
      lastSyncedAt: null,
      lastError: null,
    };
  }

  /* ─────────────────────────── lifecycle ─────────────────────────── */

  /**
   * Recover from however the last session ended, then publish real counts.
   *
   * `recoverInFlight()` is the §6.3 half nobody remembers: a row the previous
   * tab handed to the transport and never heard back about is sitting in
   * `in_flight`, present but unclaimable. Returning it to `queued` is what turns
   * "the data survived" into "the data is delivered".
   *
   * "Unclaimable" is literal, not rhetorical: `store.dueOutbox()` returns only
   * rows in state `queued`, so an abandoned claim is skipped by every drain
   * until this call clears it. That is the coupling that makes this line
   * load-bearing rather than tidy-up — delete it and the row is never sent
   * again. It is pinned by "a stubbed recoverInFlight strands the row" in
   * tests/unit/sync.test.ts, which fails if this call is removed.
   */
  async start(): Promise<SyncStatus> {
    await this.store.recoverInFlight();
    await this.store.recoverLostSubmissions();
    await this.rearmMisparked();
    this.unsubscribeStore ??= this.store.subscribe(() => {
      // A status refresh that loses its race with a page teardown is not an
      // error worth surfacing — the counters it was about to publish are gone
      // with the view that wanted them.
      void this.refreshStatus().catch(() => undefined);
    });
    return this.refreshStatus();
  }

  /**
   * Put back the rows the OLD classification parked by mistake.
   *
   * The 2026-08-10 fix stops those rows being parked in future. It does nothing
   * whatsoever for the five photos already sitting `blocked` on the phone in her
   * hand, because a parked row leaves that state only when a person taps — and
   * the person was told, wrongly, that the office could not accept the picture.
   * Asking her to find five tiles across three sections and undo a decision the
   * app made incorrectly on her behalf is not a fix, it is a chore.
   *
   * So the corrected rule is applied to the rows it was wrong about, once, on
   * start. Narrow by construction: `wasMisparked` matches only the two exact
   * messages the corrected branches used to mint, so a genuinely unsendable
   * photo — a TIFF, an oversized file — stays parked and stays the surveyor's
   * decision.
   *
   * Returns how many were re-armed, so a test can assert it did something.
   */
  async rearmMisparked(): Promise<number> {
    const parked = await this.store.listBlocked();
    let rearmed = 0;
    for (const item of parked) {
      if (!wasMisparked(item.lastError)) continue;
      if (await this.store.retryOutboxItem(item.id, this.clock())) rearmed += 1;
    }
    return rearmed;
  }

  /** Detach from the store's change feed. Does not touch persisted state. */
  stop(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
  }

  /**
   * Resolve once no drain is in progress. `setOnline(true)` starts one without
   * handing back a promise, so this is how a caller (or a test) waits for a
   * reconnect to finish draining.
   */
  async settle(): Promise<void> {
    while (this.active) {
      await this.active.catch(() => undefined);
    }
  }

  /* ──────────────────────────── status ──────────────────────────── */

  getStatus(): SyncStatus {
    return { ...this.status };
  }

  subscribe(listener: SyncStatusListener): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Re-read the counters from IndexedDB. Cheap: three indexed counts. */
  async refreshStatus(): Promise<SyncStatus> {
    const [queued, queuedPhotos, parked, lastSyncedAt] = await Promise.all([
      this.store.countOutbox(),
      this.store.countOutboxByKind('photo'),
      this.store.listBlocked(),
      this.latestSyncedAt(),
    ]);
    this.patch({
      queued,
      queuedPhotos,
      blocked: parked.length,
      attention: this.stableAttention(parked),
      ...(lastSyncedAt ? { lastSyncedAt } : {}),
    });
    return this.getStatus();
  }

  /**
   * The parked rows as an attention list, REUSING THE PREVIOUS ARRAY when
   * nothing changed.
   *
   * `patch()` decides whether to notify by comparing each field with `!==`, and
   * a freshly-built array is never `===` its predecessor. Without this, every
   * status refresh — and there is one after every write — would publish a new
   * snapshot to every subscriber and re-render the whole shell. Comparing a
   * cheap signature instead keeps the change feed honest: a notification means
   * something actually moved.
   */
  private stableAttention(parked: readonly OutboxItem[]): readonly SyncAttention[] {
    if (parked.length === 0) {
      // CLEARING THE SIGNATURE IS THE POINT, not tidiness.
      //
      // Leaving it set made the cache lie the moment a row parked, un-parked and
      // parked again with the same words — which is exactly what "Try sending it
      // again" does to a photo the office still cannot accept. The rebuilt entry
      // matched the stale signature, the `===` branch below handed back
      // `this.status.attention`, and by then that WAS this empty array. The photo
      // sat parked and silent, and the surveyor's one deliberate action made the
      // explanation disappear. Caught by tests/e2e/photo-upload.spec.ts.
      this.attentionSignature = '';
      return EMPTY_ATTENTION;
    }
    const next = parked.map<SyncAttention>((item) => {
      const status = statusFromMessage(item.lastError);
      const gone = item.lastError?.includes(PHOTO_BYTES_MISSING) === true;
      return {
        itemId: item.id,
        kind: item.kind,
        surveyId: item.surveyId,
        ...(item.photoId ? { photoId: item.photoId } : {}),
        ...(status !== undefined ? { status } : {}),
        // A stored message with a status in it came from the office; one without
        // never got that far. There is no third possibility and no flag to keep
        // in step — the presence of the number IS the evidence.
        side: status === undefined ? 'device' : 'office',
        canRetry: !gone,
        reason: plainReason(item.kind, status, item.lastError),
        detail: item.lastError ?? '',
      };
    });
    const signature = JSON.stringify(next);
    if (signature === this.attentionSignature) return this.status.attention;
    this.attentionSignature = signature;
    return next;
  }

  /**
   * Connectivity. Reported by the host (see `attachBrowserConnectivity`) rather
   * than sniffed, so the engine has no opinion about which environment it is in.
   * Coming back online kicks a drain — that is the whole point of the signal.
   */
  setOnline(online: boolean): void {
    const wasOnline = this.status.online;
    this.patch({ online });
    if (online && !wasOnline) void this.drain().catch(() => undefined);
  }

  private patch(changes: Partial<SyncStatus>): void {
    const next: SyncStatus = { ...this.status, ...changes };
    const changed = (Object.keys(next) as (keyof SyncStatus)[]).some(
      (key) => next[key] !== this.status[key],
    );
    if (!changed) return;
    this.status = next;
    const snapshot = this.getStatus();
    for (const listener of [...this.listeners]) listener(snapshot);
  }

  /** Newest ack across all surveys — survives a reload because it is stored. */
  private async latestSyncedAt(): Promise<string | null> {
    const surveys = await this.store.listSurveys();
    let latest: string | null = null;
    for (const survey of surveys) {
      const at = survey.lastSyncedAt;
      if (at && (latest === null || at > latest)) latest = at;
    }
    return latest;
  }

  /* ──────────────────────────── the drain ──────────────────────────── */

  /**
   * Send everything that is due, in order, one row at a time.
   *
   * Each row is independent: its own try/catch, its own backoff. A failure
   * neither aborts the pass nor removes the row, so the worst case for any
   * single call is "this one is still queued and will be retried later".
   */
  async drain(): Promise<DrainReport> {
    // A skipped drain still reports the true queue depth: the pill must not read
    // "0 waiting" just because this particular call declined to run.
    if (this.active) {
      return {
        ran: false,
        skipped: 'already_draining',
        ...emptyCounts(),
        remaining: await this.store.countOutbox(),
        errors: [],
      };
    }
    if (!this.status.online) {
      return {
        ran: false,
        skipped: 'offline',
        ...emptyCounts(),
        remaining: await this.store.countOutbox(),
        errors: [],
      };
    }

    // Assigned before the first await inside runDrain resolves, so a second
    // caller in the same tick sees the guard.
    const running = this.runDrain();
    this.active = running;
    try {
      return await running;
    } finally {
      this.active = null;
    }
  }

  private async runDrain(): Promise<DrainReport> {
    this.patch({ syncing: true });

    let attempted = 0;
    let delivered = 0;
    let deferred = 0;
    let failed = 0;
    const errors: DrainError[] = [];

    try {
      const due = await this.store.dueOutbox(this.clock());
      for (const item of due) {
        attempted += 1;
        try {
          await this.store.markInFlight(item.id);
          const outcome = await this.send(item);
          if (outcome === 'deferred') deferred += 1;
          else delivered += 1;
        } catch (error) {
          failed += 1;
          const message = describeError(error);
          const retryable = error instanceof TransportError ? error.retryable : true;
          const parked = shouldParkRow(error);
          errors.push({ itemId: item.id, kind: item.kind, message, retryable, parked });
          await this.recordFailure(item, message, parked);
        }
      }
    } finally {
      // Parked rows are excluded from `lastError` deliberately. They are not a
      // transient link problem and they are not going to clear on their own, so
      // letting one pin the pill red would produce a warning the surveyor can
      // never dismiss — and a warning that can never be dismissed is a warning
      // that gets ignored, including the next time it is real. They are reported
      // through `attention` instead, which carries an action.
      const transient = errors.filter((entry) => entry.parked !== true);
      const lastError =
        transient.length > 0 ? (transient[transient.length - 1]?.message ?? null) : null;
      this.patch({ syncing: false, lastError });
      await this.refreshStatus().catch(() => undefined);
    }

    return {
      ran: true,
      attempted,
      delivered,
      deferred,
      failed,
      remaining: await this.store.countOutbox(),
      errors,
    };
  }

  private async send(item: OutboxItem): Promise<SendOutcome> {
    switch (item.kind) {
      case 'create':
        await this.sendCreation(item);
        return 'delivered';
      case 'document':
        await this.sendDocument(item);
        return 'delivered';
      case 'submission':
        return this.sendSubmission(item);
      case 'seal':
        await this.sendSeal(item);
        return 'delivered';
      case 'discard':
        await this.sendDiscard(item);
        return 'delivered';
      case 'photo':
        return this.sendPhoto(item);
      case 'photo_thumb':
        return this.sendPhotoThumb(item);
    }
  }

  /**
   * Keep the row, bump the attempt count, and either schedule the retry or PARK
   * it. A row is never removed here — that is the property the whole design
   * rests on, and parking does not weaken it: the row, the PhotoRef and the
   * bytes all survive, they simply stop being re-sent.
   */
  private async recordFailure(
    item: OutboxItem,
    message: string,
    parked: boolean,
  ): Promise<void> {
    const attempts = item.attempts + 1;
    if (parked) {
      await this.store.parkOutboxItem(item.id, message);
    } else {
      const nextAttemptAt = this.clock() + backoffDelayMs(attempts, this.backoff);
      await this.store.failOutboxItem(item.id, message, nextAttemptAt);
    }
    if (item.kind === 'photo' && item.photoId) {
      // Mirror the transport state onto the PhotoRef so the section screen can
      // show the surveyor which shot has not landed yet.
      await this.store.setPhotoUploadState(item.surveyId, item.photoId, 'failed', attempts);
    }
  }

  /* ─────────────────── the surveyor's deliberate retry ─────────────────── */

  /**
   * Try one parked row again, because a person asked.
   *
   * This is the ONLY way a parked row goes back on the queue, and that is the
   * design: an automatic retry is what produced forty-two identical 400s. A
   * deliberate one is different — the surveyor may have deleted and re-taken the
   * photo, or the office may have fixed something, or they may simply want to
   * see it fail with their own eyes before they call in. Either way it is one
   * request, once, not a loop.
   *
   * Returns false when the row is gone or was not parked, so a double-tap on a
   * slow phone is a no-op rather than a second send.
   */
  async retryBlocked(itemId: string): Promise<boolean> {
    const requeued = await this.store.retryOutboxItem(itemId, this.clock());
    if (!requeued) {
      await this.refreshStatus().catch(() => undefined);
      return false;
    }
    await this.refreshStatus().catch(() => undefined);
    if (this.status.online) await this.drain().catch(() => undefined);
    return true;
  }

  /** `retryBlocked` addressed by photo, which is what a photo tile knows. */
  async retryBlockedPhoto(photoId: string): Promise<boolean> {
    return this.retryBlocked(outboxId.photo(photoId));
  }

  /* ───────────────────────────── senders ───────────────────────────── */

  /**
   * Spec §2.1's creation notice — "Chip and Tim are notified immediately … no
   * job may exist they don't know about".
   *
   * Its own row, drained first, deleted only by its own ack. Immediately means
   * the first moment there is a network, which is the only meaning available in
   * a basement; what this guarantees is that the moment arrives however long it
   * takes and whatever else the survey has done in the meantime — including
   * being finished and submitted before the phone ever sees a bar of signal.
   */
  private async sendCreation(item: OutboxItem): Promise<void> {
    const record = await this.store.getSurvey(item.surveyId);
    // The survey it announces no longer exists, or is not one the office needs
    // announcing. Nothing to say and nothing to retry.
    if (!record) return this.store.dropOutboxItem(item.id);
    if (record.document.origin !== 'field_initiated') {
      return this.store.dropOutboxItem(item.id);
    }

    const result = await this.transport.createSurvey({
      idempotencyKey: idempotencyKeyFor(item),
      surveyId: item.surveyId,
      document: record.document,
      deviceId: this.deviceId,
    });

    void result.outcome; // 'created' and 'already_created' settle identically
    await this.store.ackCreation(item.surveyId);
  }

  /**
   * Document push. The row is a pointer, so the payload is read HERE, at send
   * time — never a snapshot taken at enqueue time that has since gone stale.
   */
  private async sendDocument(item: OutboxItem): Promise<void> {
    const record = await this.store.getSurvey(item.surveyId);
    if (!record) return this.store.dropOutboxItem(item.id);
    if (record.sealed === 1) return this.store.dropOutboxItem(item.id);
    // A pointer can be pointing at a document that has since been SUBMITTED —
    // by a row queued before the submit, or by a build that predates the guard
    // in `queueDocumentPush`. `PUT /api/surveys/:id` answers a submitted
    // document with 422 `use_submit_endpoint`, always, so sending it is a
    // request that cannot succeed and whose only lasting effect is to put the
    // sync pill into its alarm state. The submission row carries this content.
    if (isSubmittedStatus(record.document.status)) {
      return this.store.dropOutboxItem(item.id);
    }

    const sentRevision = record.localRevision;
    const result = await this.transport.pushDocument({
      idempotencyKey: idempotencyKeyFor({ ...item, localRevision: sentRevision }),
      surveyId: item.surveyId,
      localRevision: sentRevision,
      document: record.document,
      deviceId: this.deviceId,
    });

    if (result.outcome === 'sealed') {
      // The server refused because the survey is submitted. Nothing local is
      // discarded; the device simply stops offering revisions it cannot take.
      await this.store.markSealed(item.surveyId, result.serverRevision);
      return;
    }

    if (result.outcome === 'discarded') {
      // The survey was withdrawn on the server (D-012) — by this surveyor on
      // another device, or by the office. No revision will ever be accepted for
      // it again, so the row is dropped rather than retried into a permanent
      // alarm on the sync pill.
      //
      // Nothing local is destroyed, exactly as for `sealed`. This device still
      // holds the surveyor's copy and they can withdraw it themselves, which
      // replays as `already_discarded` and settles. Deleting their work here on
      // the strength of a remote decision they may not have made is not this
      // engine's call.
      await this.store.dropOutboxItem(item.id);
      return;
    }

    // 'stored' and 'unchanged' are the same thing to the device: the server has
    // this revision. 'unchanged' is what a replayed lost ack looks like, and it
    // is a success, not an error.
    await this.store.ackDocumentPush(item.surveyId, sentRevision, result.serverRevision);
  }

  private async sendSubmission(item: OutboxItem): Promise<SendOutcome> {
    const record = await this.store.getSurvey(item.surveyId);
    if (!record) {
      await this.store.dropOutboxItem(item.id);
      return 'delivered';
    }

    // D-050: the whole package, or not yet. The office builds the PDF the
    // moment a submission lands, and the design authority ruled that it sees
    // a survey only when every picture is there. A photo row for this survey
    // still in the outbox — queued, failing, or parked on the Needs-you
    // screen — holds the submission; it goes the moment the last one does.
    // The draft revisions already travelled, so the office holds the content
    // meanwhile; only the seal waits.
    if (await this.photosPending(item.surveyId)) {
      await this.store.deferOutboxItem(item.id, this.clock() + backoffDelayMs(1, this.backoff));
      return 'deferred';
    }

    const result = await this.transport.submitSurvey({
      idempotencyKey: idempotencyKeyFor(item),
      surveyId: item.surveyId,
      document: record.document,
      deviceId: this.deviceId,
    });

    // 'already_submitted' is the replay case and acks exactly like a first
    // submission — the survey is sealed either way.
    await this.store.ackSubmission(item.surveyId, result.serverRevision);
    return 'delivered';
  }

  /** Whether any photo of this survey is still to be sent (D-050). */
  private async photosPending(surveyId: string): Promise<boolean> {
    return (await this.photosOwed(surveyId)) > 0;
  }

  /** How many photos of this survey are still to be sent — queued, failing, or parked. */
  private async photosOwed(surveyId: string): Promise<number> {
    const rows = await this.store.listOutboxFor(surveyId);
    return rows.filter((row) => row.kind === 'photo').length;
  }

  /**
   * Tell the office the survey is sealed on this phone and how many pictures
   * are still to come (D-052 §3).
   *
   * Grand Dunes, 2026-09-22: sealed on the phone at 8:29 PM, two photos short
   * all night, and the office page read *In progress* until 9:46 the next
   * morning — the same words it uses for a survey nobody has finished. This
   * row goes ahead of the photos and says which it is. It is dropped unsent
   * when no photo is owed, because the submission then goes in this very pass
   * and says more; and it is dropped, not failed, when the office answers
   * that it no longer holds the survey or already holds it sealed.
   */
  private async sendSeal(item: OutboxItem): Promise<void> {
    const record = await this.store.getSurvey(item.surveyId);
    if (!record || !isSubmittedStatus(record.status) || record.sealed === 1) {
      await this.store.dropOutboxItem(item.id);
      return;
    }
    const photosOwed = await this.photosOwed(item.surveyId);
    if (photosOwed === 0) {
      await this.store.dropOutboxItem(item.id);
      return;
    }
    await this.transport.notifySealed({
      idempotencyKey: idempotencyKeyFor(item),
      surveyId: item.surveyId,
      sealedAt: record.document.submittedAt ?? isoAt(this.clock()),
      photosOwed,
      deviceId: this.deviceId,
    });
    await this.store.dropOutboxItem(item.id);
  }

  /**
   * Tell the server a survey has been withdrawn (D-012).
   *
   * The ONLY sender that does not read a local record first, and it must stay
   * that way: `SurveyStore.discardSurvey` deleted the document in the same
   * transaction that queued this row, so "the survey is not on this device" is
   * the normal state here, not a reason to drop the row. Treating a missing
   * record the way `sendDocument` does would delete every discard before it was
   * ever sent, and the office would keep a job the surveyor withdrew — the
   * quiet failure this row exists to prevent.
   *
   * All three outcomes settle identically. `already_discarded` is the replay
   * after a lost ack; `already_gone` is a server that has no such survey, which
   * is the same news.
   */
  private async sendDiscard(item: OutboxItem): Promise<void> {
    const result = await this.transport.discardSurvey({
      idempotencyKey: idempotencyKeyFor(item),
      surveyId: item.surveyId,
      deviceId: this.deviceId,
    });

    void result.outcome;
    await this.store.ackDiscard(item.surveyId);
  }

  /**
   * One photo, alone, last. Bytes come from `photoBlobs`; metadata comes from
   * the document's PhotoRef. Neither is duplicated into the outbox row, so a
   * queued photo costs one small record no matter how large the image is.
   */
  private async sendPhoto(item: OutboxItem): Promise<SendOutcome> {
    const photoId = item.photoId;
    if (!photoId) {
      await this.store.dropOutboxItem(item.id);
      return 'delivered';
    }

    const record = await this.store.getSurvey(item.surveyId);
    const photo = record?.document.photos.find((p) => p.id === photoId);
    if (!record || !photo) {
      // The capture it referred to is gone — the surveyor deleted the photo, or
      // the whole survey. Nothing to upload and nothing to retry: this is the
      // only non-ack removal (see dropOutboxItem).
      await this.store.dropOutboxItem(item.id);
      return 'delivered';
    }

    /*
     * ── THE PHOTO THAT GOT AHEAD OF ITS DOCUMENT ────────────────────────────
     *
     * `POST /api/photos` answers 409 `survey_not_synced` when the office holds
     * no row for the survey, because the FK on photos.survey_id needs one. The
     * drain already orders document before photos, so this is not the ordinary
     * case — it is what happens when the document push FAILED earlier in this
     * same pass (a dropped connection on the PUT) and the photo was attempted
     * anyway, seconds later, over the same dead link.
     *
     * That round trip cannot succeed, and it is the most expensive request in
     * the system: several megabytes uploaded to be refused. So the photo is put
     * back rather than sent. `neverReachedServer` is the device's own record of
     * exactly the condition the 409 reports — a field-initiated survey with no
     * acknowledged creation, no acknowledged revision and no seal — and it is
     * already the predicate D-012/D-018 trust for "the office has never heard of
     * this". Nothing new is being guessed here.
     *
     * Deferring is not failing: attempts are not bumped, no error is recorded,
     * nothing turns red. The row simply waits for the document that is queued
     * ahead of it.
     */
    if (neverReachedServer(record)) {
      await this.store.deferOutboxItem(
        item.id,
        this.clock() + backoffDelayMs(1, this.backoff),
      );
      return 'deferred';
    }

    const stored = await this.store.getPhotoBlob(photoId);
    if (!stored) {
      /*
       * THE BYTES ARE GONE AND THE PHOTOREF IS NOT.
       *
       * This used to drop the outbox row, silently. The document kept a PhotoRef
       * whose tile read "Will retry" and nothing was ever going to retry it: the
       * only row that could have carried those bytes had just been deleted to
       * tidy the queue. Photos are the deliverable for FLS (Amendment 5), so a
       * photo that quietly stops existing is a lost deliverable — the exact
       * failure mode this engine is built to make impossible everywhere else.
       *
       * iOS evicts IndexedDB blobs under storage pressure and five 4 MB captures
       * is enough pressure, so this is a field condition, not a corner case. It
       * PARKS, which keeps the row, and it parks with a device-side reason so
       * the tile can say the picture is gone, the survey is not, and the one
       * thing that helps is taking this one again.
       */
      throw new TransportError(
        `${PHOTO_BYTES_MISSING}: the picture file is no longer in this device's storage`,
        { retryable: false, code: PHOTO_BYTES_MISSING },
      );
    }

    /*
     * READ THE PICTURE, AND CHECK IT IS ALL THERE (2026-08-19).
     *
     * The transport used to be handed the stored Blob — a handle — and on an
     * iPhone running the installed app that handle serialised as an empty body:
     * every upload arrived at the office as a multipart header and zero bytes
     * (src/db/photo-file.ts, module header). The bytes are read HERE, in the
     * page's own memory. Empty is the same finding as missing: nothing a retry
     * can change, so it parks, loudly, with the one action that helps — take it
     * again — while the capture path (PhotoCapture) now refuses a File it
     * cannot read before it is ever stored, so a fresh capture cannot arrive
     * here empty. (Only EMPTY is parked here. The byte count the device read is
     * what it declares to the office, so a body that does not all arrive is the
     * office's `incomplete_upload` and a retry, not a park.)
     *
     * Since schema version 2 (round 8.1) the row on disk is an `ArrayBuffer`
     * and `stored.blob` is a Blob `getPhotoBlob` built from it in this page's
     * memory, so this read is a copy of bytes already here; it is kept because
     * the empty check is the rule, whatever the row's value type.
     */
    const data = await readPhotoBytes(stored.blob);
    if (data.byteLength === 0) {
      throw new TransportError(
        `${PHOTO_BYTES_MISSING}: the picture file on this device is empty (0 of ${String(photo.bytes)} bytes)`,
        { retryable: false, code: PHOTO_BYTES_MISSING },
      );
    }

    await this.store.setPhotoUploadState(item.surveyId, photoId, 'uploading', item.attempts);

    const result = await this.transport.uploadPhoto({
      idempotencyKey: idempotencyKeyFor(item),
      surveyId: item.surveyId,
      photoId,
      sectionInstanceId: photo.sectionInstanceId,
      ...(photo.questionId ? { questionId: photo.questionId } : {}),
      ...(photo.shotListId ? { shotListId: photo.shotListId } : {}),
      ...(photo.label ? { label: photo.label } : {}),
      r2Key: photo.r2Key,
      contentType: photo.contentType,
      bytes: data.byteLength,
      capturedAt: photo.capturedAt,
      data,
    });

    void result.outcome; // 'uploaded' and 'already_uploaded' settle identically
    await this.store.ackPhotoUpload(item.surveyId, photoId);
    return 'delivered';
  }

  /**
   * One thumbnail, after its photo (round 8.1, task 3).
   *
   * Queued behind the photo's own row and sent after it, so in an ordinary
   * pass the picture and the document that names it have both been acked by
   * the time this runs. When either has NOT — the upload or the push failed
   * earlier in this pass, or one of them is parked — the thumb is put back
   * unsent and unbumped, exactly as a photo waits for its document: the
   * office files a thumb under the head document's own key for the photo and
   * refuses one for a picture that document does not name. Deferring is not
   * failing, and a parked photo or document keeps its thumb waiting for as
   * long as it is parked.
   *
   * What cannot be sent parks THIS row and touches nothing else: a thumb row
   * this phone no longer holds, or an office that answers 404 or 409, is
   * `PHOTO_THUMB_MISSING` — a device-side reason, so the tile never turns a
   * delivered photo red for a preview it can live without.
   */
  private async sendPhotoThumb(item: OutboxItem): Promise<SendOutcome> {
    const photoId = item.photoId;
    if (!photoId) {
      await this.store.dropOutboxItem(item.id);
      return 'delivered';
    }

    const record = await this.store.getSurvey(item.surveyId);
    const photo = record?.document.photos.find((p) => p.id === photoId);
    if (!record || !photo) {
      // The picture it belonged to is gone — deleted, or its survey was — so
      // there is no call to make: the same non-ack removal `sendPhoto` uses.
      await this.store.dropOutboxItem(item.id);
      return 'delivered';
    }

    // The photo's own row is still here: it has not been acked, whether it
    // is queued, in flight after a failure, or parked. The thumb waits.
    if ((await this.store.getOutboxItem(outboxId.photo(photoId))) !== undefined) {
      await this.store.deferOutboxItem(item.id, this.clock() + backoffDelayMs(1, this.backoff));
      return 'deferred';
    }

    /*
     * THE DOCUMENT IS WHAT CARRIES THE PHOTO TO THE OFFICE (fix round 1).
     *
     * `POST /photos` files the bytes and a `photos` row; it never writes the
     * head document. Only `PUT /surveys/:id` does, and that is the push the
     * thumb route reads the PhotoRef — and the key — from. So a photo can be
     * at the office while its survey's head document does not yet name it:
     * the document push failed earlier in this same pass on a survey the
     * office already knew, and `sendPhoto` guards only `neverReachedServer`.
     * A thumb sent now is answered 404, and 404 must not be read as "never".
     * While the document row is here — queued, under backoff, or parked —
     * the thumb waits behind it, unsent and unbumped, the same way it waits
     * behind its photo.
     */
    if (await this.documentPending(item.surveyId)) {
      await this.store.deferOutboxItem(item.id, this.clock() + backoffDelayMs(1, this.backoff));
      return 'deferred';
    }

    /*
     * AND FOR A SUBMITTED SURVEY, THE SUBMISSION IS THAT DOCUMENT (D-052 §4).
     *
     * A submitted document never travels as a push — `sendDocument` drops it
     * and the seal carries it — so until the submission lands the office's
     * head document does not name this photo at all, whatever `POST /photos`
     * filed. The submission waits behind every photo of its survey (D-050);
     * a thumb whose own picture landed in an EARLIER pass than the last one
     * — one photo under backoff, the rest through — used to go out here, be
     * answered 404, and park on the Needs-you screen for a preview the
     * office would have filed a minute later. Found by the Grand Dunes round
     * (2026-09-23) in the mid-sync disconnect spec. While the submission row
     * is here — queued, deferred, or under backoff — the thumb waits behind
     * it, exactly as it waits behind a document push.
     */
    if (await this.submissionPending(item.surveyId)) {
      await this.store.deferOutboxItem(item.id, this.clock() + backoffDelayMs(1, this.backoff));
      return 'deferred';
    }

    const stored = await this.store.getPhotoThumb(photoId);
    if (!stored || stored.data.byteLength === 0) {
      throw new TransportError(
        `${PHOTO_THUMB_MISSING}: the small preview is no longer in this device's storage`,
        { retryable: false, code: PHOTO_THUMB_MISSING },
      );
    }

    try {
      await this.transport.uploadPhotoThumb({
        surveyId: item.surveyId,
        photoId,
        data: new Uint8Array(stored.data),
      });
    } catch (error) {
      if (error instanceof TransportError && (error.status === 404 || error.status === 409)) {
        // The office holds no photo to file this against. If a document push
        // is pending again — the surveyor edited while this was in flight and
        // the row was re-queued — the head the office holds is not the last
        // word, and the thumb waits for it rather than parking on a 404 the
        // next push will clear.
        if (await this.documentPending(item.surveyId)) {
          await this.store.deferOutboxItem(item.id, this.clock() + backoffDelayMs(1, this.backoff));
          return 'deferred';
        }
        // No document push owed and still no photo at the office: nothing on
        // this phone can change that answer. Parked as a device-side finding,
        // with the office's code kept for the log and never the status
        // number, which is what would make `attention` blame the office.
        throw new TransportError(
          `${PHOTO_THUMB_MISSING}: the office has no photo to file this preview against (${error.code ?? 'refused'})`,
          { retryable: false, code: PHOTO_THUMB_MISSING, cause: error },
        );
      }
      throw error;
    }

    await this.store.ackThumbUpload(photoId);
    return 'delivered';
  }

  /** Is a document push for this survey still owed — queued, under backoff, or parked? */
  private async documentPending(surveyId: string): Promise<boolean> {
    return (await this.store.getOutboxItem(outboxId.document(surveyId))) !== undefined;
  }

  /** Is this survey's submission still owed — queued, deferred behind its photos, or under backoff? */
  private async submissionPending(surveyId: string): Promise<boolean> {
    return (await this.store.getOutboxItem(outboxId.submission(surveyId))) !== undefined;
  }

  /* ───────────────────────────── pulling ───────────────────────────── */

  /**
   * Pull the server's copy and apply the conflict ladder to it. This is the only
   * place a server document can replace a local one, and it never does so on its
   * own authority — `resolveConflict()` decides and the decision is returned so
   * the caller (and the test) can see which rule fired.
   */
  async pull(surveyId: string): Promise<PullResult> {
    if (!this.transport.fetchDocument) {
      throw new Error('pull: this transport cannot fetch documents');
    }
    // The discard tombstone, checked before anything is fetched or decided.
    // See `reconcileAssignment` for the full reasoning; the short version is
    // that a survey with a discard still queued has been withdrawn and taking
    // the server's copy of it would put it back.
    if (await this.store.hasPendingDiscard(surveyId)) {
      const decision = DISCARD_PENDING_DECISION;
      await this.refreshStatus();
      return { applied: false, decision, serverRevision: 0 };
    }

    const remote = await this.transport.fetchDocument(surveyId);
    const local = await this.store.getSurvey(surveyId);
    const pending = await this.store.countOutboxFor(surveyId);
    const decision = resolveConflict(conflictContextFor(local, remote, pending));

    if (decision.winner === 'device' || !remote) {
      await this.refreshStatus();
      return { applied: false, decision, serverRevision: remote?.serverRevision ?? 0 };
    }

    await this.store.adoptServerDocument(remote.document, remote.serverRevision, remote.sealed);
    await this.refreshStatus();
    return { applied: true, decision, serverRevision: remote.serverRevision };
  }

  /* ─────────────────────── the assignment inbox ─────────────────────── */

  /**
   * Bring down the surveys the office has assigned to this device's surveyor.
   *
   * This is spec §2.1's PRIMARY path — "Assigned (usual)" — and until it was
   * wired up the device had no inbox at all: a survey Chip or Tim assigned in
   * the office never reached the phone, because `pull()` was called from nothing
   * outside its own unit test.
   *
   * Three properties, and all three are load-bearing:
   *
   *   1. THE LADDER DECIDES, ALWAYS. Every row goes through `resolveConflict()`
   *      before anything is written, and the winner is read off the decision —
   *      not inferred from the order the writes happen to occur in. A device
   *      that is holding unsynced work keeps it (R2 / R4 / R4b); a device that
   *      has never heard of the survey takes the server's copy (R0).
   *   2. AN ASSIGNMENT MAY BE ALMOST EMPTY. The office often has nothing but an
   *      address, and there is no server DOCUMENT until somebody works the job —
   *      only a lifecycle row. `assignmentDocument()` turns that row into an
   *      openable document so the surveyor can start on site.
   *   3. IT IS CHEAP TO RUN OFTEN. The decision is made from the LIST row
   *      (`headRevision`, `status`), so the common case — nothing changed —
   *      costs one request and no writes. The full document is fetched only when
   *      the server has one the device has not already got.
   *
   * Scoping is the server's job and is not re-checked here: `GET /api/surveys`
   * is filtered by the authenticated surveyor (`listSurveysFor`), and
   * `GET /api/surveys/:id` 403s on a survey assigned to somebody else. A device
   * cannot ask for another surveyor's work, so the inbox cannot receive it.
   */
  async pullAssignments(): Promise<AssignmentPullReport> {
    if (!this.transport.listAssignments) {
      return { ran: false, skipped: 'unsupported', ...emptyPullCounts(), errors: [] };
    }
    if (!this.status.online) {
      return { ran: false, skipped: 'offline', ...emptyPullCounts(), errors: [] };
    }
    if (this.pulling) {
      return { ran: false, skipped: 'already_pulling', ...emptyPullCounts(), errors: [] };
    }

    // Assigned before the first await inside runPull resolves, so a second
    // caller in the same tick sees the guard.
    const running = this.runPull();
    this.pulling = running;
    try {
      return await running;
    } finally {
      this.pulling = null;
    }
  }

  /** Resolve once no assignment pull is in progress. The `settle()` for pulls. */
  async settlePull(): Promise<void> {
    while (this.pulling) {
      await this.pulling.catch(() => undefined);
    }
  }

  private async runPull(): Promise<AssignmentPullReport> {
    if (!this.transport.listAssignments) {
      return { ran: false, skipped: 'unsupported', ...emptyPullCounts(), errors: [] };
    }

    let rows: RemoteAssignment[];
    try {
      // Called on the transport, not through a detached reference: an
      // ApiClient's `listAssignments` reads `this.token` and would be `undefined`
      // the moment it was pulled off the object.
      rows = await this.transport.listAssignments();
    } catch (error) {
      // A failed list is exactly as harmless as a failed drain: nothing local is
      // touched and the next trigger tries again. It is surfaced because the
      // surveyor standing in a lobby waiting for their job to appear deserves to
      // see that the device could not ask.
      const message = describeError(error);
      this.patch({ lastError: message });
      return { ran: true, ...emptyPullCounts(), errors: [message] };
    }

    let added = 0;
    let adopted = 0;
    let kept = 0;
    const errors: string[] = [];

    for (const row of rows) {
      try {
        const outcome = await this.reconcileAssignment(row);
        if (outcome === 'added') added += 1;
        else if (outcome === 'adopted') adopted += 1;
        else kept += 1;
      } catch (error) {
        // One unreadable assignment must not stop the others arriving.
        errors.push(`${row.surveyId}: ${describeError(error)}`);
      }
    }

    await this.refreshStatus().catch(() => undefined);
    return { ran: true, seen: rows.length, added, adopted, kept, errors };
  }

  /** One assignment row against whatever the device already holds. */
  private async reconcileAssignment(row: RemoteAssignment): Promise<AssignmentOutcome> {
    /*
     * THE DISCARD TOMBSTONE (D-012), and it is checked first because the whole
     * ladder below is written on the assumption that the device WANTS what the
     * server is listing.
     *
     * A discard is applied locally the instant the surveyor confirms it — the
     * document is gone from the device — and only then queued. Between those two
     * moments the server is still listing the survey, because it has not been
     * told yet, and that is not a corner case: offline, it is the whole point,
     * and the queue can sit there for hours.
     *
     * With no guard, R0 ("the device holds no copy, so taking the server's
     * cannot overwrite anything") fires, the survey is re-added, and the
     * surveyor watches the job they just withdrew walk back into their list on
     * the next 30-second tick. The queued discard is the evidence that "no local
     * copy" is a DECISION here rather than an absence, so it wins.
     *
     * Once the discard is acked the server stops listing the survey at all
     * (`listSurveysFor` filters `discarded_at IS NULL`), so the tombstone is
     * needed exactly as long as the promise is outstanding and not one tick
     * longer.
     */
    if (await this.store.hasPendingDiscard(row.surveyId)) return 'kept';

    const local = await this.store.getSurvey(row.surveyId);
    const pending = await this.store.countOutboxFor(row.surveyId);

    // Note what this context is built from: the LIST row, not a fetched
    // document. `headRevision > 0` is "the server holds a document" and the
    // lifecycle status is what "sealed" means, so the decision costs nothing.
    const decision = resolveConflict({
      localExists: local !== undefined,
      everSynced: (local?.serverRevision ?? 0) > 0,
      localDirty: (local?.dirty ?? 0) === 1,
      localPending: pending > 0,
      serverHasDocument: row.headRevision > 0,
      serverSealed: isSubmittedStatus(row.status),
    });

    if (decision.winner === 'device') return 'kept';

    if (row.headRevision > 0) {
      // Already holding this revision: adopting it again would rewrite the
      // record — and bump its revision — on every 30-second tick for nothing.
      if (local && row.headRevision <= local.serverRevision) return 'kept';

      const remote = this.transport.fetchDocument
        ? await this.transport.fetchDocument(row.surveyId)
        : null;
      if (remote) {
        await this.store.adoptServerDocument(
          remote.document,
          remote.serverRevision,
          remote.sealed,
        );
        return local ? 'adopted' : 'added';
      }
    }

    // No server document to take. If the device already has a copy, that copy is
    // the only one there is and it stays untouched.
    if (local) return 'kept';

    // A brand-new assignment with nothing behind it yet — usually the moment
    // after the office typed an address and hit assign. Adopted CLEAN: the
    // device owes the server nothing for it, so nothing is queued.
    await this.store.adoptServerDocument(assignmentDocument(row), 0, false);
    return 'added';
  }
}

/* ═════════════════════════════ helpers ═════════════════════════════ */

/** One shared frozen array, so "nothing needs attention" is reference-stable. */
const EMPTY_ATTENTION: readonly SyncAttention[] = Object.freeze([]);

/**
 * The HTTP status back out of a stored failure message.
 *
 * The outbox row keeps the message, not the status — the row is written by
 * `store.failOutboxItem`, which has no idea what HTTP is, and giving it one
 * would leak the transport into the persistence layer for one integer. The
 * messages are minted in exactly one place (`ApiClient.fail`, `"… failed (400):
 * …"`), so reading it back is a parse of our own format, not of the network's.
 * Absent or unrecognised simply yields `undefined` and the generic sentence.
 */
function statusFromMessage(message: string | undefined): number | undefined {
  if (!message) return undefined;
  const match = /\((\d{3})\)/.exec(message);
  if (!match?.[1]) return undefined;
  const status = Number(match[1]);
  return Number.isInteger(status) ? status : undefined;
}

/** What one row's send did. `deferred` is "put back unsent", not "failed". */
type SendOutcome = 'delivered' | 'deferred';

function emptyCounts(): Pick<
  DrainReport,
  'attempted' | 'delivered' | 'deferred' | 'failed' | 'remaining'
> {
  return { attempted: 0, delivered: 0, deferred: 0, failed: 0, remaining: 0 };
}

function emptyPullCounts(): Pick<AssignmentPullReport, 'seen' | 'added' | 'adopted' | 'kept'> {
  return { seen: 0, added: 0, adopted: 0, kept: 0 };
}

/**
 * The document for an assignment the server has no document for yet.
 *
 * Spec §2.1: an assignment can be nothing but an address. The office's
 * `buildingName` and `address` are not a second home for those facts — they ARE
 * answers B1 and B2, the same keys the surveyor would type them into on the
 * building screen, so opening the job shows the office's head start already
 * filled in and editing it is an ordinary answer write. The city, state and
 * ZIP (D-039) are B2a, B2b and B2c the same way — and only when the office
 * typed them, so a thin row seeds no blank answers.
 *
 * Pure, and reads no clock: the timestamps come off the assignment row, so the
 * survey's `createdAt` is when the office created it and not when this phone
 * happened to hear about it.
 */
export function assignmentDocument(row: RemoteAssignment): SurveyDocument {
  const document = emptySurveyDocument({
    surveyId: row.surveyId,
    origin: row.origin,
    status: row.status,
    createdAt: row.createdAt,
    ...(row.assignedTo ? { assignedTo: row.assignedTo } : {}),
    ...(row.createdBy ? { createdBy: row.createdBy } : {}),
  });
  if (row.buildingName) document.answers['B1'] = { kind: 'text', value: row.buildingName };
  if (row.address) document.answers['B2'] = { kind: 'text', value: row.address };
  if (row.city) document.answers['B2a'] = { kind: 'text', value: row.city };
  if (row.state) document.answers['B2b'] = { kind: 'text', value: row.state };
  if (row.zip) document.answers['B2c'] = { kind: 'text', value: row.zip };
  document.updatedAt = row.updatedAt;
  return document;
}

export function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'unknown sync error';
}

/**
 * Wire the engine to the browser's connectivity events. Guarded so importing
 * this module in a Worker or under Node is harmless — src/ ships to both.
 */
export function attachBrowserConnectivity(engine: SyncEngine): () => void {
  if (typeof globalThis.addEventListener !== 'function') return () => {};
  const nav = (globalThis as { navigator?: { onLine?: boolean } }).navigator;
  if (typeof nav?.onLine === 'boolean') engine.setOnline(nav.onLine);

  const goOnline = (): void => engine.setOnline(true);
  const goOffline = (): void => engine.setOnline(false);
  globalThis.addEventListener('online', goOnline);
  globalThis.addEventListener('offline', goOffline);
  return () => {
    globalThis.removeEventListener('online', goOnline);
    globalThis.removeEventListener('offline', goOffline);
  };
}

/** Re-exported so callers wire one import for the whole persistence layer. */
export { SurveyStore, outboxId, isoAt, systemClock };
export type { Clock, OutboxItem, OutboxKind, StoredSurvey };
